import React, { useCallback, useEffect, useRef, useState } from "react";
import { createUpload } from "@mux/upchunk";
import MuxPlayer from "@mux/mux-player-react";
import { ArrowDown, ArrowUp, Check, ChevronLeft, ChevronRight, Film, FolderOpen, GripVertical, LoaderCircle, Pencil, Play, Search, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { EmptyState, Modal, btnGhost, btnPrimary, fmtDuration, inputCls } from "./shared";

const TYPES = [
  ["cut", "Cut", "Cuts"], ["part", "Part", "Parts"], ["extra", "Extra", "Extras"], ["raw", "Raw", "Raw"],
];
const FILTERS = [["all", "All"], ["cut", "Cuts"], ["part", "Parts"], ["extra", "Extras"], ["raw", "Raw"]];
const STATUS_OPTIONS = ["all", "ready", "preparing", "errored"];
const API_PATH = (videoId) => `/api/videos/${videoId}/media-assets`;
const prettyStatus = (status) => ({
  uploading: "Uploading", processing: "Processing", ready: "Ready", failed: "Failed",
  preparing: "Processing", errored: "Failed", asset_created: "Processing",
}[status] || status || "Unknown");
const typeLabel = (type) => TYPES.find(([value]) => value === type)?.[1] || "Media";
const thumbnail = (asset) => asset.posterUrl || (asset.muxPlaybackId
  ? `https://image.mux.com/${asset.muxPlaybackId}/thumbnail.jpg?width=480&height=270&fit_mode=smartcrop&time=2`
  : "");

function safeDate(value) {
  if (!value) return "Date unavailable";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Date unavailable" : date.toLocaleDateString();
}

function MuxLibraryModal({ videoId, type, onClose, onLinked }) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [pageStack, setPageStack] = useState([]);
  const [items, setItems] = useState([]);
  const [nextPage, setNextPage] = useState(null);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState("");
  const page = pageStack.at(-1) || "";

  useEffect(() => {
    let active = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const response = await api.get(`${API_PATH(videoId)}/mux-library`, {
          params: { limit: 24, page: page || undefined, status: status === "all" ? undefined : status, search: query.trim() },
        });
        if (!active) return;
        setItems(response.data?.items || []);
        setNextPage(response.data?.nextPage || null);
        setSelected([]);
      } catch (requestError) {
        if (!active) return;
        setError(requestError.response?.data?.detail || "Could not load the Mux library");
        setItems([]);
        setNextPage(null);
      } finally {
        if (active) setLoading(false);
      }
    }, 250);
    return () => { active = false; clearTimeout(timer); };
  }, [videoId, query, status, page]);

  const resetPages = (setter) => (value) => {
    setter(value);
    setPageStack([]);
  };

  const linkSelected = async () => {
    if (!selected.length) return;
    setLinking(true);
    try {
      const response = await api.post(`${API_PATH(videoId)}/mux-library/link`, { type, assetIds: selected });
      onLinked(response.data?.items || []);
      toast.success(`${selected.length} Mux asset${selected.length === 1 ? "" : "s"} linked`);
      onClose();
    } catch (requestError) {
      toast.error(requestError.response?.data?.detail || "Could not link selected Mux assets");
    } finally {
      setLinking(false);
    }
  };

  return (
    <Modal title="Choose from Mux Library" onClose={onClose} wide
      footer={<>
        <button type="button" className={btnGhost} onClick={onClose}>Cancel</button>
        <button type="button" className={btnPrimary} disabled={!selected.length || linking} onClick={linkSelected}>
          {linking ? "Linking..." : `Link selected${selected.length ? ` (${selected.length})` : ""}`}
        </button>
      </>}>
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_180px]">
        <label className="relative block">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input className={`${inputCls} pl-9`} value={query} onChange={(event) => resetPages(setQuery)(event.target.value)} placeholder="Search title or Mux ID" />
        </label>
        <select className={inputCls} aria-label="Mux status filter" value={status} onChange={(event) => resetPages(setStatus)(event.target.value)}>
          {STATUS_OPTIONS.map((option) => <option key={option} value={option}>{option === "all" ? "All statuses" : prettyStatus(option)}</option>)}
        </select>
      </div>
      <div className="text-xs text-zinc-500">Link as <span className="font-semibold text-cyan-200">{typeLabel(type)}</span></div>
      {error && <p role="alert" className="rounded border border-red-500/30 bg-red-950/20 p-3 text-sm text-red-200">{error}</p>}
      {loading ? <div className="py-10 text-center text-sm text-zinc-500"><LoaderCircle className="mr-2 inline animate-spin" size={16} />Loading Mux assets...</div>
        : !items.length ? <EmptyState>No Mux assets match.</EmptyState> : (
          <div className="grid max-h-[52vh] gap-2 overflow-y-auto sm:grid-cols-2">
            {items.map((item) => {
              const checked = selected.includes(item.id);
              return (
                <button key={item.id} type="button" disabled={item.linked}
                  onClick={() => setSelected((current) => checked ? current.filter((id) => id !== item.id) : [...current, item.id])}
                  className={`flex min-w-0 items-center gap-3 rounded-lg border p-2 text-left transition ${checked ? "border-purple-400 bg-purple-950/30" : "border-zinc-800 bg-black/30 hover:border-zinc-600"} disabled:cursor-not-allowed disabled:opacity-50`}>
                  {item.posterUrl ? <img src={item.posterUrl} alt="" className="aspect-video w-28 shrink-0 rounded bg-black object-cover" />
                    : <div className="flex aspect-video w-28 shrink-0 items-center justify-center rounded bg-zinc-900"><Film size={18} className="text-zinc-600" /></div>}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-white">{item.title || item.id}</span>
                    <span className="block truncate text-[11px] text-zinc-500" title={item.id}>{item.id}</span>
                    <span className="mt-1 flex flex-wrap gap-x-2 text-[10px] text-zinc-400">
                      <span>{fmtDuration(item.duration) || "Duration unknown"}</span><span>{safeDate(item.createdAt)}</span><span>{prettyStatus(item.status)}</span>
                    </span>
                    {item.linked && <span className="mt-1 block text-[10px] text-cyan-300">Already linked to this video</span>}
                  </span>
                  <span className={`grid h-5 w-5 shrink-0 place-items-center rounded border ${checked ? "border-purple-400 bg-purple-500 text-white" : "border-zinc-600 text-transparent"}`}><Check size={12} /></span>
                </button>
              );
            })}
          </div>
        )}
      <div className="flex items-center justify-between border-t border-zinc-800 pt-3">
        <button type="button" className={btnGhost} disabled={!pageStack.length || loading}
          onClick={() => setPageStack((current) => current.slice(0, -1))}><ChevronLeft size={14} className="inline" /> Previous</button>
        <span className="text-xs text-zinc-500">Page {pageStack.length + 1}</span>
        <button type="button" className={btnGhost} disabled={!nextPage || loading}
          onClick={() => setPageStack((current) => [...current, nextPage])}>Next <ChevronRight size={14} className="inline" /></button>
      </div>
    </Modal>
  );
}

function MediaAssetRow({ asset, progress, onPreview, onEdit, onDelete, onMove, onDragStart, onDrop, onDragOver }) {
  return (
    <article draggable onDragStart={(event) => onDragStart(event, asset)} onDragOver={onDragOver} onDrop={(event) => onDrop(event, asset)}
      className="flex min-w-0 flex-col gap-3 rounded-lg border border-zinc-800 bg-black/40 p-3 sm:flex-row sm:items-center"
      data-testid={`media-asset-${asset.id}`}>
      <button type="button" draggable={false} aria-label={`Reorder ${asset.title}`} title="Drag to reorder" className="hidden cursor-grab touch-none text-zinc-500 active:cursor-grabbing sm:block">
        <GripVertical size={18} />
      </button>
      {thumbnail(asset) ? <img src={thumbnail(asset)} alt="" className="aspect-video w-full rounded bg-black object-cover sm:w-32" />
        : <div className="flex aspect-video w-full items-center justify-center rounded bg-zinc-900 sm:w-32"><Film size={21} className="text-zinc-600" /></div>}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <h4 className="max-w-full truncate text-sm font-semibold text-white">{asset.title}</h4>
          <span className="rounded border border-cyan-500/30 px-1.5 py-0.5 text-[9px] uppercase text-cyan-200">{typeLabel(asset.type)}</span>
          {asset.isLegacy && <span className="rounded border border-pink-500/30 px-1.5 py-0.5 text-[9px] uppercase text-pink-200">Legacy</span>}
          <span className={`text-[10px] ${asset.status === "ready" ? "text-emerald-300" : asset.status === "failed" ? "text-red-300" : "text-amber-200"}`}>
            {asset.status === "uploading" && <LoaderCircle size={11} className="mr-1 inline animate-spin" />}{prettyStatus(asset.status)}
          </span>
        </div>
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-zinc-500">
          <span>{fmtDuration(asset.durationSeconds) || "Duration unknown"}</span>
          {asset.source && <span>{asset.source === "mux_library" ? "Mux library" : "Device upload"}</span>}
          {asset.muxAssetId && <span className="max-w-[220px] truncate" title={asset.muxAssetId}>ID {asset.muxAssetId}</span>}
        </div>
        {progress && <div className="mt-2 max-w-md" aria-live="polite">
          <div className="mb-1 flex justify-between text-[10px] text-zinc-400"><span>{progress.phase === "processing" ? "Processing on Mux" : "Uploading"}</span><span>{Math.round(progress.value)}%</span></div>
          <div className="h-1.5 overflow-hidden rounded bg-zinc-800"><div className="h-full bg-cyan-400 transition-[width]" style={{ width: `${progress.value}%` }} /></div>
        </div>}
      </div>
      <div className="flex shrink-0 items-center justify-end gap-1 border-t border-zinc-800 pt-2 sm:border-0 sm:pt-0">
        <button type="button" title="Move up" aria-label={`Move ${asset.title} up`} onClick={() => onMove(asset, -1)} className="rounded p-2 text-zinc-500 hover:bg-zinc-800 hover:text-white"><ArrowUp size={14} /></button>
        <button type="button" title="Move down" aria-label={`Move ${asset.title} down`} onClick={() => onMove(asset, 1)} className="rounded p-2 text-zinc-500 hover:bg-zinc-800 hover:text-white"><ArrowDown size={14} /></button>
        <button type="button" title="Preview" aria-label={`Preview ${asset.title}`} disabled={!asset.muxPlaybackId} onClick={() => onPreview(asset)} className="rounded p-2 text-cyan-300 hover:bg-cyan-950 disabled:cursor-not-allowed disabled:opacity-30"><Play size={15} /></button>
        <button type="button" title="Edit title, type or poster" aria-label={`Edit ${asset.title}`} onClick={() => onEdit(asset)} className="rounded p-2 text-zinc-300 hover:bg-zinc-800"><Pencil size={15} /></button>
        <button type="button" title="Delete" aria-label={`Delete ${asset.title}`} onClick={() => onDelete(asset)} className="rounded p-2 text-zinc-500 hover:bg-red-950 hover:text-red-300"><Trash2 size={15} /></button>
      </div>
    </article>
  );
}

export default function MediaAssetsTab({ videoId, disabled = false }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [uploadType, setUploadType] = useState("cut");
  const [dropActive, setDropActive] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [posterUploading, setPosterUploading] = useState(false);
  const [editValues, setEditValues] = useState({ title: "", type: "cut", posterUrl: "" });
  const [progress, setProgress] = useState({});
  const [preview, setPreview] = useState(null);
  const [draggedAsset, setDraggedAsset] = useState(null);
  const inputRef = useRef(null);
  const uploads = useRef(new Map());
  const localPhases = useRef({});

  const loadAssets = useCallback(async () => {
    if (!videoId || disabled) { setLoading(false); return; }
    try {
      const response = await api.get(API_PATH(videoId));
      const rows = response.data?.items || [];
      setAssets((current) => {
        const serverIds = new Set(rows.map((row) => row.id));
        const refreshed = rows.map((row) => {
          const local = localPhases.current[row.id];
          if (row.status === "ready" || row.status === "failed") delete localPhases.current[row.id];
          return local?.phase === "processing" && row.status === "uploading" ? { ...row, status: "processing" } : row;
        });
        const pending = current.filter((row) => !serverIds.has(row.id) && ["uploading", "processing"].includes(row.status));
        return [...refreshed, ...pending];
      });
      setLoading(false);
    } catch (requestError) {
      toast.error(requestError.response?.data?.detail || "Could not load media assets");
      setLoading(false);
    }
  }, [videoId, disabled]);

  useEffect(() => { loadAssets(); }, [loadAssets]);

  useEffect(() => {
    if (!assets.some((asset) => asset.status === "uploading" || asset.status === "processing")) return undefined;
    const timer = setInterval(loadAssets, 5000);
    return () => clearInterval(timer);
  }, [assets, loadAssets]);

  const updateAsset = (assetId, patch) => setAssets((current) => current.map((asset) => asset.id === assetId ? { ...asset, ...patch } : asset));

  const startUploads = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    await Promise.all(files.map(async (file) => {
      const title = file.name.replace(/\.[^.]+$/, "").trim() || file.name;
      try {
        const response = await api.post(API_PATH(videoId), {
          type: uploadType, title, filename: file.name, contentType: file.type || "application/octet-stream", fileSizeBytes: file.size,
        });
        const { asset, uploadUrl } = response.data;
        setAssets((current) => [...current, asset]);
        setProgress((current) => ({ ...current, [asset.id]: { phase: "uploading", value: 0 } }));
        const upload = createUpload({ endpoint: uploadUrl, file, chunkSize: 5120, dynamicChunkSize: true });
        uploads.current.set(asset.id, upload);
        upload.on("progress", (event) => setProgress((current) => ({
          ...current, [asset.id]: { phase: "uploading", value: Math.max(0, Math.min(100, Number(event.detail) || 0)) },
        })));
        upload.on("success", () => {
          uploads.current.delete(asset.id);
          localPhases.current[asset.id] = { phase: "processing" };
          setProgress((current) => ({ ...current, [asset.id]: { phase: "processing", value: 100 } }));
          updateAsset(asset.id, { status: "processing" });
          toast.message(`${title} uploaded; Mux is processing it`);
          loadAssets();
        });
        upload.on("error", async (event) => {
          uploads.current.delete(asset.id);
          localPhases.current[asset.id] = { phase: "failed" };
          const message = event.detail?.message || "Upload failed";
          setProgress((current) => ({ ...current, [asset.id]: { phase: "failed", value: 0 } }));
          updateAsset(asset.id, { status: "failed" });
          try { await api.post(`${API_PATH(videoId)}/${asset.id}/upload-fail`, { error: message }); } catch { /* Preserve the local failure state if offline. */ }
          toast.error(`${title}: ${message}`);
        });
      } catch (requestError) {
        toast.error(requestError.response?.data?.detail || `Could not start upload for ${file.name}`);
      }
    }));
  };

  const openEdit = (asset) => {
    setEditing(asset.id);
    setEditValues({ title: asset.title || "", type: asset.type || "cut", posterUrl: asset.posterUrl || "" });
  };

  const saveEdit = async () => {
    try {
      const response = await api.patch(`${API_PATH(videoId)}/${editing}`, editValues);
      updateAsset(editing, response.data);
      setEditing(null);
      toast.success("Media asset updated");
    } catch (requestError) {
      toast.error(requestError.response?.data?.detail || "Could not update media asset");
    }
  };

  const deleteAsset = async (asset) => {
    if (!window.confirm(`Delete “${asset.title}” from this video?`)) return;
    try {
      await api.delete(`${API_PATH(videoId)}/${asset.id}`);
      setAssets((current) => current.filter((row) => row.id !== asset.id));
      setProgress((current) => { const next = { ...current }; delete next[asset.id]; return next; });
      toast.success("Media asset deleted");
    } catch (requestError) {
      toast.error(requestError.response?.data?.detail || "Could not delete media asset");
    }
  };

  const showPreview = async (asset) => {
    try {
      const response = await api.get(`${API_PATH(videoId)}/${asset.id}/playback`);
      setPreview({ ...response.data, title: asset.title });
    } catch (requestError) {
      toast.error(requestError.response?.data?.detail || "Could not prepare playback");
    }
  };

  const saveOrder = async (type, rows) => {
    const ordered = rows.map((asset, sortOrder) => ({ ...asset, sortOrder }));
    setAssets((current) => current.map((asset) => ordered.find((item) => item.id === asset.id) || asset));
    try {
      await api.post(`${API_PATH(videoId)}/reorder`, { type, assetIds: ordered.map((asset) => asset.id) });
    } catch (requestError) {
      toast.error(requestError.response?.data?.detail || "Could not save asset order");
      loadAssets();
    }
  };

  const moveAsset = (asset, offset) => {
    const typeRows = assets.filter((row) => row.type === asset.type).sort((a, b) => a.sortOrder - b.sortOrder);
    const from = typeRows.findIndex((row) => row.id === asset.id);
    const to = from + offset;
    if (from < 0 || to < 0 || to >= typeRows.length) return;
    const reordered = [...typeRows];
    [reordered[from], reordered[to]] = [reordered[to], reordered[from]];
    saveOrder(asset.type, reordered);
  };

  const uploadPoster = async (file) => {
    if (!file || !editing) return;
    if (!file.type.startsWith("image/")) { toast.error("Choose an image file"); return; }
    if (file.size > 50 * 1024 * 1024) { toast.error("Poster images must be smaller than 50 MB"); return; }
    setPosterUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("folder", "videos/linked-photos");
      form.append("resource_type", "image");
      const response = await api.post("/api/cloudinary/upload", form);
      setEditValues((current) => ({ ...current, posterUrl: response.data.secure_url }));
      toast.success("Poster uploaded. Save changes to apply it.");
    } catch (requestError) {
      toast.error(requestError.response?.data?.detail || "Could not upload poster");
    } finally {
      setPosterUploading(false);
    }
  };

  const handleDrop = (event, asset) => {
    event.preventDefault();
    if (!draggedAsset || draggedAsset.type !== asset.type || draggedAsset.id === asset.id) return;
    const typeRows = assets.filter((row) => row.type === asset.type).sort((a, b) => a.sortOrder - b.sortOrder);
    const from = typeRows.findIndex((row) => row.id === draggedAsset.id);
    const to = typeRows.findIndex((row) => row.id === asset.id);
    if (from < 0 || to < 0) return;
    const reordered = [...typeRows];
    reordered.splice(from, 1);
    reordered.splice(to, 0, draggedAsset);
    setDraggedAsset(null);
    saveOrder(asset.type, reordered);
  };

  const addLinkedAssets = (rows) => {
    setAssets((current) => {
      const byId = new Map(current.map((asset) => [asset.id, asset]));
      rows.forEach((asset) => byId.set(asset.id, asset));
      return [...byId.values()];
    });
  };

  const visibleTypes = filter === "all" ? TYPES.map(([type]) => type) : [filter];
  const grouped = visibleTypes.map((type) => ({
    type,
    assets: assets.filter((asset) => asset.type === type).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)),
  }));

  return (
    <div className="space-y-5" data-testid="media-assets-tab">
      <header className="space-y-1">
        <h2 className="text-lg font-bold text-white">Media Assets</h2>
        <p className="text-sm text-zinc-400">Upload and manage cuts, parts, extras, and raw footage in one place.</p>
      </header>

      {disabled ? <EmptyState>Create this video first to add media assets.</EmptyState> : <>
        <section className="rounded-xl border border-zinc-800 bg-black/40 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <label className="block w-full sm:max-w-xs">
              <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-zinc-400">Asset type</span>
              <select className={inputCls} value={uploadType} onChange={(event) => setUploadType(event.target.value)}>
                {TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            <div className="grid gap-2 sm:grid-cols-2">
              <button type="button" className={btnPrimary} onClick={() => inputRef.current?.click()}><Upload size={15} className="mr-1.5 inline" />Upload Media</button>
              <button type="button" className={btnGhost} onClick={() => setLibraryOpen(true)}><FolderOpen size={15} className="mr-1.5 inline" />Choose from Mux</button>
              <input ref={inputRef} type="file" accept="video/*,.avi,.m4v,.mov,.mp4,.webm" multiple className="hidden" disabled={disabled}
                onChange={(event) => { startUploads(event.target.files); event.target.value = ""; }} />
            </div>
          </div>
          <div onDragEnter={(event) => { event.preventDefault(); setDropActive(true); }}
            onDragOver={(event) => event.preventDefault()} onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setDropActive(false); }}
            onDrop={(event) => { event.preventDefault(); setDropActive(false); startUploads(event.dataTransfer.files); }}
            className={`mt-4 flex min-h-20 cursor-pointer items-center justify-center rounded-lg border border-dashed px-4 py-4 text-center text-xs transition ${dropActive ? "border-cyan-400 bg-cyan-950/30 text-cyan-100" : "border-zinc-700 text-zinc-500 hover:border-zinc-500"}`}
            onClick={() => inputRef.current?.click()}>
            <span><Upload size={15} className="mr-2 inline text-cyan-300" />Drop one or more video files here, or choose files from your device.</span>
          </div>
        </section>

        <nav className="flex gap-2 overflow-x-auto border-b border-zinc-800 pb-2" aria-label="Media asset filters">
          {FILTERS.map(([value, label]) => <button key={value} type="button" aria-pressed={filter === value} onClick={() => setFilter(value)}
            className={`shrink-0 rounded-md border px-3 py-1.5 text-xs font-medium ${filter === value ? "border-purple-400 bg-purple-950/40 text-white" : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-white"}`}>{label}</button>)}
        </nav>

        {loading ? <div className="py-8 text-center text-sm text-zinc-500"><LoaderCircle size={16} className="mr-2 inline animate-spin" />Loading media assets...</div>
          : assets.length === 0 ? <EmptyState>No media assets yet.</EmptyState> : (
            <div className="space-y-5">
              {grouped.map(({ type, assets: rows }) => rows.length > 0 && (
                <section key={type} className="space-y-2" aria-label={`${typeLabel(type)} assets`}>
                  {filter === "all" && <h3 className="text-xs font-semibold uppercase text-zinc-500">{TYPES.find(([value]) => value === type)?.[2]}</h3>}
                  {rows.map((asset) => <MediaAssetRow key={asset.id} asset={asset} progress={progress[asset.id]}
                    onPreview={showPreview} onEdit={openEdit} onDelete={deleteAsset} onMove={moveAsset}
                    onDragStart={(event, row) => { event.dataTransfer.effectAllowed = "move"; setDraggedAsset(row); }}
                    onDragOver={(event) => event.preventDefault()} onDrop={handleDrop} />)}
                </section>
              ))}
              {!grouped.some(({ assets: rows }) => rows.length) && <EmptyState>No assets in this filter.</EmptyState>}
            </div>
          )}
      </>}

      {libraryOpen && <MuxLibraryModal videoId={videoId} type={uploadType} onClose={() => setLibraryOpen(false)} onLinked={addLinkedAssets} />}
      {editing && <Modal title="Edit Media Asset" onClose={() => !posterUploading && setEditing(null)} footer={<>
        <button type="button" className={btnGhost} disabled={posterUploading} onClick={() => setEditing(null)}>Cancel</button>
        <button type="button" className={btnPrimary} onClick={saveEdit} disabled={posterUploading}>Save changes</button>
      </>}>
        <label className="block text-xs text-zinc-400">Title<input className={`${inputCls} mt-1`} value={editValues.title} onChange={(event) => setEditValues((value) => ({ ...value, title: event.target.value }))} /></label>
        <label className="block text-xs text-zinc-400">Type<select className={`${inputCls} mt-1`} value={editValues.type} onChange={(event) => setEditValues((value) => ({ ...value, type: event.target.value }))}>{TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        <div className="space-y-2">
          <label className="block text-xs text-zinc-400">Replace poster<input className="mt-1 block w-full text-xs text-zinc-400 file:mr-3 file:rounded file:border-0 file:bg-zinc-800 file:px-3 file:py-2 file:text-xs file:text-zinc-200" type="file" accept="image/*" disabled={posterUploading} onChange={(event) => { uploadPoster(event.target.files?.[0]); event.target.value = ""; }} /></label>
          {posterUploading && <span className="text-xs text-cyan-200"><LoaderCircle size={13} className="mr-1 inline animate-spin" />Uploading poster...</span>}
          <label className="block text-xs text-zinc-500">Or use image URL<input className={`${inputCls} mt-1`} type="url" placeholder="https://" value={editValues.posterUrl} onChange={(event) => setEditValues((value) => ({ ...value, posterUrl: event.target.value }))} /></label>
        </div>
      </Modal>}
      {preview && <Modal title={preview.title} onClose={() => setPreview(null)} wide>
        <MuxPlayer playbackId={preview.playback_id} tokens={preview.tokens} streamType="on-demand" className="w-full aspect-video" accent-color="#d946ef" />
      </Modal>}
    </div>
  );
}